import { PatientAssessmentCardCta, PatientAssessmentCardProps, ProviderAssessmentCardProps } from 'types/cardProps';
import { AssessmentResultRow } from 'types/tableProps';
import { AssessmentData } from 'interfaces/assessments/assessmentData';
import { AssessmentTypes, ProviderAssessmentTypes, InstrumentTypes, AppliedWhen } from 'constants/assessment_types';
import { AssessmentResultsWithExtremeDefinition } from 'domain/Forms/MHO/AssessmentResultsDefinition';

/**
 * Convert API Data to UI Props and other UI related format
 */
class ApiDataUiConverter {
  /**
   * Parses out Assessment Data retrieved from the API to Assessment Card Prop format.
   *
   * @param assessmentList list of API assessment data.
   * @param assessmentFilter optional filter to narrow the type 'ToDo', 'SelfCheck' and 'History'.
   * @returns list of assessment card props converted from the API data.
   */
  static convertAssessmentsToCardProps(assessmentList: AssessmentData[], assessmentFilter?: AssessmentTypes): PatientAssessmentCardProps[] {
    const filtler = assessmentFilter || AssessmentTypes.HISTORY;
    return assessmentList
      .filter((assessment) => {
        switch (filtler) {
          // return 'TO-DO' assessments.
          case AssessmentTypes.DAILY:
          case AssessmentTypes.FOLLOWUP:
            return assessment.sortOrder === 1 || assessment.sortOrder === 2;

          // Return self check assessments only.
          case AssessmentTypes.SELFCHECK:
            return assessment.sortOrder === 3;

          // History is unhandled
          case AssessmentTypes.HISTORY:
          default:
            return true;
        }
      })
      .map((assessment) => ({
        id: assessment.assessmentNumber,
        appliedWhenID: assessment.appliedWhenID,
        title: assessment.assessmentDescription,
        type: assessment.instrumentType,
        typeID: assessment.instrumentTypeID,
        careID: assessment.careID,
        patientID: assessment.patientID,
        assessmentNumber: assessment.assessmentNumber,
        cta: filtler === AssessmentTypes.HISTORY ? PatientAssessmentCardCta.view : PatientAssessmentCardCta.start,
        lastCompleted: assessment.dateProcessed,
        isTodo: assessment.sortOrder === 1 || assessment.sortOrder === 2,
      }));
  }

  /**
   * Returns a list of CTA's for a particular assessment (the links on the assessment cards).
   *
   * @param assessmentFilter Whether the assessment is categorized under "To-Do", "Submitted", or "History."
   * @param isCoreMeasuresMode if the api was called using the /CoreMeasures mode.
   * @param isSubmissionFrozen Whether submission for that particular patient is frozen or not. If so, they cannot complete any CM assessments.
   * @returns list of CTA's for an assessment.
   */
  private static readonly FALLS_ASSESSMENT_NUMBER = 100043;

  static getCtas(
    assessmentFilter: ProviderAssessmentTypes | undefined,
    isCoreMeasuresMode: boolean | undefined,
    assessment: AssessmentData,
  ): string[] {
    let ctas: string[] = [];
    if (assessment.assessmentNumber === this.FALLS_ASSESSMENT_NUMBER && assessment.appliedWhenID !== AppliedWhen.ADMISSION && assessment.assessmentStatus === 'Completed') {
      ctas = ['Results'];
    } else if (assessmentFilter === ProviderAssessmentTypes.SUBMITTED && assessment.instrumentTypeID !== InstrumentTypes.PIX && (!isCoreMeasuresMode || !assessment.submissionFrozen)) {
      const retakeAssessmentAction = isCoreMeasuresMode ? 'Edit' : 'Redo'; // Text depends on whether it's a CM assessment or not
      ctas = ['Results', retakeAssessmentAction];
    } else if (assessment.instrumentTypeID === InstrumentTypes.PIX && assessmentFilter === ProviderAssessmentTypes.TODO) {
      ctas = ['Tablet mode'];
    } else if (assessmentFilter === ProviderAssessmentTypes.HISTORY || (assessmentFilter === ProviderAssessmentTypes.SUBMITTED && assessment.submissionFrozen)) {
      ctas = ['Results'];
    } else if (assessment.instrumentTypeID !== InstrumentTypes.PIX && (!isCoreMeasuresMode || !assessment.submissionFrozen)) {
      ctas = ['Start'];
    }
    return ctas;
  }

  /**
   * Parses out Assessment Data retrieved from the API to Assessment Card Prop format.
   *
   * @param assessmentList list of API assessment data.
   * @param assessmentFilter optional filter to narrow the type 'ToDo', 'SelfCheck' and 'History'.
   * @param isCoreMeasuresMode if the api was called using the /CoreMeasures mode, set all isCoreMeasures fields to true. Otherwise, need to look at instrumentSetup.
   * @returns list of assessment card props converted from the API data.
   */
  static convertProviderAssessmentsToCardProps(assessmentList: AssessmentData[], assessmentFilter?: ProviderAssessmentTypes, isCoreMeasuresMode?: boolean): ProviderAssessmentCardProps[] {
    return assessmentList
      .filter((assessment) => {
        switch (assessmentFilter) {
          case ProviderAssessmentTypes.TODO:
            return assessment.assessmentStatus === '' || assessment.assessmentStatus === 'Incomplete';

          case ProviderAssessmentTypes.SUBMITTED:
            return assessment.assessmentStatus === 'Completed';
          default:
            return true;
        }
      })
      .map((assessment) => ({
        id: assessment.assessmentNumber,
        title: assessment.assessmentDescription,
        appliedWhen: assessment.appliedWhen,
        appliedWhenID: assessment.appliedWhenID,
        instrumentTypeID: assessment.instrumentTypeID,
        instrumentType: assessment.instrumentType,
        assessmentNumber: assessment.assessmentNumber.toString(),
        careID: assessment.careID,
        patientID: assessment.patientID,
        ctas: this.getCtas(assessmentFilter, isCoreMeasuresMode, assessment),
        completed: assessmentFilter === ProviderAssessmentTypes.TODO ? '' : assessment.dateProcessed,
        dateAdmitted: assessmentFilter === ProviderAssessmentTypes.TODO ? '' : assessment.dateAdmitted,
        dateDischarged: assessmentFilter === ProviderAssessmentTypes.TODO ? '' : assessment.dateDischarged,
        assessmentLinkSentDate: assessment.assessmentLinkSentDate,
        assessmentLinkEmail: assessment.assessmentLinkEmail,
        availableForPatient: assessment.availableForPatient,
        isCoreMeasures: isCoreMeasuresMode || assessment.instrumentSetup === 'CoreMeasures',
        sequenceNumber: assessment.sequenceNumber,
        assessmentStatus: assessment.assessmentStatus,
        dischargeDisp: assessment.dcDispo ? assessment.dcDispo : null,
        submissionFrozen: assessment.submissionFrozen ? assessment.submissionFrozen : null,
        awidEditable: assessment.awidEdit && assessment.awidEdit === 1,
        showEdit: false,
        documentID: assessment.documentID,
        showDelete: false,
      }));
  }

  /**
   * Checks and gets the list of assessment card props from the API assessment list.
   *
   * @param assessmentList list of API assessment list. Could be null.
   * @param assessmentTypeFilter optional filter to narrow the type 'ToDo', 'SelfCheck' and 'History'.
   * @returns a converted API assessment list to Card Prop.
   */
  static getProviderAssessmentCardProps(assessmentList: AssessmentData[] | null, assessmentTypeFilter?: ProviderAssessmentTypes, isCoreMeasuresMode?: boolean): ProviderAssessmentCardProps[] {
    let cardProps: ProviderAssessmentCardProps[] = [];
    if (assessmentList) {
      cardProps = ApiDataUiConverter.convertProviderAssessmentsToCardProps(assessmentList, assessmentTypeFilter, isCoreMeasuresMode);
    }
    return cardProps;
  }

  static convertAssessmentQuestionToTableProp(results: AssessmentResultsWithExtremeDefinition[]): AssessmentResultRow[] {
    return results.map((result: AssessmentResultsWithExtremeDefinition) => ({
      name: result.variableName,
      question: result.questionNumber !== 0 ? `${result.questionNumber}. ${result.questionText}` : result.questionText,
      selectedAnswer: '---',
      selectedExtremeMessage: '',
      latestAnswer: String(result.responseText || '---'),
      latestExtremeMessage: result.extremeResponseMessage,
      sequenceNumber: result.sequenceNumber,
      resultsBeginDate: '',
      resultsEndDate: '',
    }));
  }
}

export default ApiDataUiConverter;
